/* eslint-disable @next/next/no-img-element */
/* eslint-disable max-lines */
import { useRouter } from 'next/router';
import Button from '@base/Button/Button';
import H1 from '@base/H1/H1';
import P1 from '@base/P1/P1';
import TextField from '@base/TextField/TextField';
import Link from '@base/Link/Link';
import Dynamic from '@base/Dynamic/Dynamic';
import ImageCarousel from '@comp/ImageCarousel/ImageCarousel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Calendar from '@icon/Calendar';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import uniq from 'lodash/uniq';
import { useState, useEffect, useCallback } from 'react';
import { setLocalPhoneNumber } from '@actions/auth';
import Image from '@base/Image';
import H5 from '@base/H5/H5';
import Tags from '@base/Tags/Tags';
import { getTimeForDate, pluralize } from '@utils/textHelper';
import { isSameDay } from '@utils/dates';
import { segment } from '@actions/analytics';
import { GTM_TAP_ENROLL } from '@constants/segment';
import { toggleEnrolledCourse } from '@actions/courses';
import CountryMenu from '@cont/Login/CountryMenu';
import { getIsLoggedIn } from '@api/selectors/auth';
import useScholarshipTestInfo from '@api/hooks/scholarship/useScholarshipTestInfo';
import useScholarshipEventDetails from '@api/hooks/scholarship/useScholarshipEventDetails';
import { evtNameCategoryMap } from '@constants/index';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import { useIsMobile } from '@stores/AppCommonData';
import useCountries from '@api/hooks/useCountries';
import OneLinkSmartScript from '@Analytics/OneLinkSmartScript';
import { SCHOLARSHIP_TEST_CLICKED } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/constants/segment';
import useFollowGoal from '@api/hooks/useFollowGoal';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { sortingLogic } from './utils';

const EnrollForFreeDrawer = Dynamic(() => import('./EnrollForFreeDrawer'));
const AdditionalInputs = Dynamic(() => import('./AdditionalInputs'));
const ShareDrawer = Dynamic(() => import('./ShareDrawer'));

const Wrapper = styled.div`
  height: 700px;
  background: ${({ $bgColor }) =>
    $bgColor ||
    'radial-gradient(100% 100% at 50% 100%, var(--color-i-violet-8) 0%, var(--color-i-black-4) 100%), linear-gradient(0deg, var(--color-i-white), var(--color-i-white))'};
  position: relative;
  overflow: hidden;
  ${mediaQuery.uptoTablet} {
    height: unset;
    padding: ${Spacings.SPACING_4B};
  }
`;

const Ellipsis = styled.div`
  img {
    bottom: -130% !important;
    left: -98% !important;
    object-fit: cover;
    object-position: top;
    ${mediaQuery.uptoTablet} {
      top: -55% !important;
      right: -90% !important;
      object-position: right;
    }
  }
`;

const UNLogo = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_6B};
`;

const ContentWrapper = styled.div`
  max-width: 1136px;
  margin: auto;
`;

const SyllabusText = styled(P1)`
  width: 80%;
`;

const Flex = styled.div`
  display: flex;

  ${mediaQuery.uptoTablet} {
    flex-direction: column;
  }
`;

const Pointers = styled.div`
  display: flex;
  align-items: flex-start;
  margin-top: ${Spacings.SPACING_4B};
`;

const LeftContent = styled.div`
  flex: 1;
  h1 {
    font-size: 40px;
    color: var(--color-base-1);
    margin-top: ${Spacings.SPACING_4B};
    ${mediaQuery.uptoTablet} {
      font-size: 32px;
    }
  }
  p {
    font-size: ${Fonts.LARGE};
    color: var(--color-base-1);
    margin-left: ${Spacings.SPACING_4B};
  }
`;

const ScholarshipImageWrapper = styled.div`
  position: relative;
  height: 100px;
  width: auto;
  img {
    object-fit: contain;
    object-position: left;
  }
`;

const EnrollButton = styled(Button)`
  margin-top: ${({ $isLoggedIn }) =>
    $isLoggedIn ? Spacings.SPACING_14B : Spacings.SPACING_4B};
  width: 356px;
  font-size: ${Fonts.LARGE};
  :hover {
    box-shadow: none;
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
`;

const BannerImage = styled.div`
  position: relative;
  width: inherit;
  height: 500px;
  border-radius: 8px;
  ${mediaQuery.uptoTablet} {
    img {
      height: fit-content !important;
    }
  }
`;

const RightContent = styled.div`
  flex: 1;
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_8B};
    width: 100%;
  }
`;

const PhoneNumberField = styled(TextField)`
  width: 356px;
  background-color: var(--color-base-1);
  margin-top: ${Spacings.SPACING_10B};
  p {
    color: var(--color-text-primary);
    margin-left: 0px;
  }
  ${mediaQuery.uptoTablet} {
    width: 100%;
  }
`;

const LiveFlex = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_4B};
  h5 {
    margin-left: ${Spacings.SPACING_2B};
  }
`;

const Header = ({ enrolledEventIds = [], setEnrolledEventIds }) => {
  const {
    query: { eventKey },
    pathname
  } = useRouter();
  const isDstPage = pathname.includes('dailyscholarship');
  const { prodigyInfo } = useScholarshipTestInfo(eventKey);
  const { prodigyDetails: extraDetailsPresent } =
    useScholarshipEventDetails(eventKey);

  const {
    logo,
    title,
    scheduleText,
    syllabusText,
    carousel,
    events,
    themeBg,
    additionalInputs,
    goalUid,
    isManualEnroll
  } = prodigyInfo;

  const dispatch = useDispatch();
  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isMobile = useIsMobile();
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const [phone, onPhoneChange] = useState('');
  const [courseDrawerOpen, setCourseDrawerOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [additionalDetailsOpen, setAdditionalDetailsOpen] = useState(false);
  const goals = uniq(events.map((el) => el.goalKey)) || [];
  const eventsData = sortingLogic(events, goals);

  const isEnrolledInAny = enrolledEventIds.length > 0;
  const [currGoal, setCurrGoal] = useState(goals[0]);
  const [currEvent, setCurrentEvent] = useState(null);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [enrollClicked, setEnrollClicked] = useState(false);
  const [totalLive, setTotalLive] = useState(null);

  useEffect(() => {
    const enrolledGoalKey = events.find((event) =>
      enrolledEventIds.includes(event.testSeriesUid)
    )?.goalKey;
    if (enrolledGoalKey) setCurrGoal(enrolledGoalKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolledEventIds]);

  const countries = useCountries();
  const [country, setCountry] = useState(
    countries.length
      ? countries.find((c) => c.code === 'IN')
      : {
          code: 'IN',
          name: 'India',
          flagIconUrl:
            'https://static.uacdn.net/thumbnail/country-flag-icons/in.png',
          phoneCode: '91'
        }
  );

  function gtag_report_conversion() {
    if (window.gtag)
      window.gtag('event', 'conversion', {
        send_to: 'AW-10852057838/ferOCMqC3_sYEO6F1bYo', // pragma:allowlist secret
        event_callback: () => {}
      });
    return false;
  }

  const changeCountry = (countryOption) => {
    setCountry(countries.find((c) => c.code === countryOption.value));
  };

  const enrollAction = useCallback(
    (event = currEvent) => {
      dispatch(
        toggleEnrolledCourse(event.testSeriesUid, true, true, eventKey)
      ).then((response) => {
        if (!response.error) {
          setShareDialogOpen(true);
          setEnrollClicked(false);
          if (setEnrolledEventIds)
            setEnrolledEventIds((ids) =>
              ids.includes(event.testSeriesUid)
                ? ids
                : [...ids, event.testSeriesUid]
            );
          const analyticsProps = {
            test_name: event.testName,
            test_uid: event.testUid,
            start_time: event.startsAt,
            end_time: event.endsAt,
            Subject: event.topics,
            test_series_uid: event.testSeriesUid,
            gtm_initiative: title || '',
            goal_id: '',
            goal_name: ''
          };
          segment.track(GTM_TAP_ENROLL, analyticsProps);
          gtag_report_conversion();
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currEvent, dispatch, eventKey, title, setEnrolledEventIds]
  );

  const onClickEnroll = useCallback(
    (event = currEvent) => {
      if (!event || enrolledEventIds.includes(event.testSeriesUid)) return;
      setEnrollClicked(true);
      setCurrentEvent(event);
      if (
        !(
          additionalInputs?.length &&
          !extraDetailsPresent?.userResponse?.questionsSubmitted
        ) &&
        event
      ) {
        enrollAction(event);
      }
    },
    [
      currEvent,
      additionalInputs,
      extraDetailsPresent,
      enrollAction,
      enrolledEventIds
    ]
  );

  const onNumberChange = (e) => {
    const number = e.target.value.replace(/ /g, '');
    if (number.match(/^\d+$/) || number === '') {
      if (number.length > 10) {
        // do nothing
      } else {
        onPhoneChange(number);
      }
    }
  };

  const validMobileNumber = useCallback(() => {
    if (phone.match(/^\d+$/) && phone.length !== 10) {
      return false;
    }
    if (phone === '') {
      return false;
    }
    return true;
  }, [phone]);

  const { followCurrentGoal } = useFollowGoal(goalUid);

  const onEnrollClick = useCallback(() => {
    if (isLoggedIn) {
      followCurrentGoal();
      if (enrolledEventIds.length) {
        setEnrollClicked(false);
        setCourseDrawerOpen(true);
        return;
      }
      setEnrollClicked(true);
      if (
        additionalInputs?.length &&
        !extraDetailsPresent?.userResponse?.questionsSubmitted
      ) {
        setAdditionalDetailsOpen(true);
      } else {
        setCourseDrawerOpen(true);
      }
    } else if (!isLoggedIn && validMobileNumber()) {
      dispatch(setLocalPhoneNumber(phone, country));
      openLoginDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    additionalInputs?.length,
    country,
    dispatch,
    enrolledEventIds.length,
    extraDetailsPresent?.userResponse?.questionsSubmitted,
    isLoggedIn,
    phone,
    validMobileNumber
  ]);
  const onGoToClick = useCallback(() => {
    const { goalKey, goalName } = events[0];
    segment.track(
      SCHOLARSHIP_TEST_CLICKED,
      {
        last_primary_source: 'scholarship landing page',
        goal_name: goalName,
        goal_uid: goalKey,
        is_reattempt: false
      },
      evtNameCategoryMap.WITHOUT_CATEGORY
    );
    // if (isMobile) {
    //   window.open(appLink, '_blank');
    //   return;
    // }
    window.open(events[0].permalink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    setTotalLive(
      events.filter(
        (event) =>
          event.date &&
          isSameDay(new Date(event.date), new Date()) &&
          new Date(getTimeForDate(event.startsAt, event.date)) - new Date() <
            1800000 &&
          new Date(getTimeForDate(event.endsAt, event.date)) - new Date() > 0
      )?.length || 0
    );
  }, [events]);

  useEffect(() => {
    if (enrollClicked && isLoggedIn) {
      onEnrollClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  if (!eventsData[currGoal]) return null;

  const renderCarousel = () => {
    return (
      <ImageCarousel>
        {carousel.map((banner, i) => {
          return (
            <BannerImage
              key={`banner-${banner.id}`}
              onClick={
                banner.permalink ? () => window.open(banner.permalink) : null
              }
            >
              <Image
                priority
                src={isMobile ? banner.mwImage : banner.webImage}
                alt={`banner-${i}`}
                layout="fill"
              />
            </BannerImage>
          );
        })}
      </ImageCarousel>
    );
  };

  return (
    <Wrapper $bgColor={themeBg}>
      <Ellipsis>
        <Image src="prodigy/ellipsis.png" layout="fill" alt="" />
      </Ellipsis>
      <ContentWrapper>
        <Link href="/" as="/">
          <UNLogo>
            <Image
              priority
              src="prodigy/un_logo.svg"
              alt="logo"
              width={161}
              height={24}
            />
          </UNLogo>
        </Link>
        <Flex>
          <LeftContent>
            <ScholarshipImageWrapper>
              <Image priority src={logo} alt="logo" layout="fill" />
            </ScholarshipImageWrapper>
            <H1>{title}</H1>
            <Pointers>
              <Image
                src="prodigy/book-outline.svg"
                alt="book"
                width={24}
                height={24}
              />
              <SyllabusText lineClamp={3}>{syllabusText}</SyllabusText>
            </Pointers>
            <Pointers>
              <Calendar size="24px" color="var(--color-base-1)" />
              <P1>{scheduleText}</P1>
            </Pointers>
            <>
              {!isLoggedIn && !isDstPage && (
                <PhoneNumberField
                  fullWidth
                  startAdornment={
                    <InputAdornment>
                      <CountryMenu
                        changeCountry={changeCountry}
                        country={country}
                        countries={countries}
                      />
                    </InputAdornment>
                  }
                  placeholder="10 digit mobile number"
                  onChange={onNumberChange}
                  value={phone}
                  inputProps={{
                    type: 'tel',
                    maxLength: 15,
                    minLength: 3,
                    required: true
                  }}
                  type="tel"
                />
              )}
              {isDstPage ? (
                <EnrollButton
                  label="Attempt test"
                  grid="5"
                  $isLoggedIn={isLoggedIn}
                  onClick={onGoToClick}
                />
              ) : (
                <EnrollButton
                  label={
                    isEnrolledInAny
                      ? 'View your enrollments'
                      : 'Enroll for free'
                  }
                  grid="5"
                  $isLoggedIn={isLoggedIn}
                  onClick={
                    !isLoggedIn && phone.length === 0 ? () => {} : onEnrollClick
                  }
                />
              )}
            </>

            {totalLive ? (
              <LiveFlex>
                <Tags type="live">LIVE</Tags>
                <H5 color="var(--color-base-1)">{`${pluralize(
                  totalLive,
                  'test'
                )} live right now`}</H5>
              </LiveFlex>
            ) : null}
          </LeftContent>
          <RightContent>{renderCarousel()}</RightContent>
        </Flex>
      </ContentWrapper>
      {courseDrawerOpen && !isDstPage && (
        <EnrollForFreeDrawer
          enrolledEventIds={enrolledEventIds}
          setCurrentEvent={setCurrentEvent}
          setCourseDrawerOpen={setCourseDrawerOpen}
          courseDrawerOpen={courseDrawerOpen}
          setDrawerOpen={setDrawerOpen}
          goals={goals}
          currGoal={currGoal}
          setCurrGoal={setCurrGoal}
          eventsData={eventsData}
          currEvent={currEvent}
          isDrawerOpen={isDrawerOpen}
          onClickEnroll={onClickEnroll}
          isEnrollClicked={enrollClicked}
          isManualEnroll={isManualEnroll}
        />
      )}
      {additionalDetailsOpen && (
        <AdditionalInputs
          drawerState={additionalDetailsOpen}
          closeDrawerHandler={() => setAdditionalDetailsOpen(false)}
          additionalInputs={additionalInputs}
          eventKey={eventKey}
          events={events}
          callback={(matchedEvent) => {
            if (matchedEvent) {
              setCurrentEvent(matchedEvent);
              enrollAction(matchedEvent);
              return;
            }
            setCourseDrawerOpen(true);
          }}
        />
      )}
      {currEvent && shareDialogOpen && (
        <ShareDrawer
          showShareDialog={shareDialogOpen}
          closeShareDialog={() => setShareDialogOpen(false)}
          hideCloseButton
          showBackButton
          event={currEvent}
          enrolledEventIds={enrolledEventIds}
        />
      )}
      {true && <OneLinkSmartScript />}
    </Wrapper>
  );
};

export default Header;
